import React, { useState, useEffect, useMemo } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  LayoutTemplate, Clock, CheckCircle2, XCircle,
} from 'lucide-react';
import { documentsAPI, esignAPI } from '@/lib/api';
import PrepareDocumentModal from './PrepareDocumentModal';
import CountersignModal from './CountersignModal';
import SubmissionViewerModal from './SubmissionViewerModal';
import ManageSigningModal from './ManageSigningModal';
import DocumentPreviewModal from './DocumentPreviewModal';
import { toast } from 'sonner';
import { safeFormat } from '@/lib/utils';

// ── ESignBadge ────────────────────────────────────────────────────────────────

const ESIGN_STATUS = {
  pending:             { label: 'Awaiting Signature', icon: Clock,         color: 'bg-amber-50 text-amber-700 border-amber-300' },
  partially_signed:    { label: 'In Progress',        icon: Clock,         color: 'bg-amber-50 text-amber-700 border-amber-300' },
  awaiting_execution:  { label: 'Awaiting Your Part', icon: CheckCircle2,  color: 'bg-blue-50 text-blue-700 border-blue-300' },
  executed:            { label: 'Fully Executed',     icon: CheckCircle2,  color: 'bg-green-50 text-green-700 border-green-300' },
  declined:            { label: 'Declined',           icon: XCircle,       color: 'bg-red-50 text-red-600 border-red-300' },
};

const ESignBadge = ({ status }) => {
  const info = ESIGN_STATUS[status];
  if (!info) return null;
  const Icon = info.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${info.color}`}>
      <Icon className="w-3 h-3" />
      {info.label}
    </span>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
// Its own top-level nav destination (not a Documents tab) — most documents
// will never go through e-signature at all, so someone looking specifically
// for this shouldn't have to know it's buried inside Documents.

export default function ESignature() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signDoc, setSignDoc] = useState(null);
  const [templateDoc, setTemplateDoc] = useState(null);
  const [countersignId, setCountersignId] = useState(null);
  const [viewSubId, setViewSubId] = useState(null);
  const [manageSub, setManageSub] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);

  const templates = useMemo(() => docs.filter((d) => d.is_template), [docs]);

  const loadDocs = async () => {
    setLoading(true);
    try {
      const res = await documentsAPI.list();
      setDocs(res.data);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    setSubsLoading(true);
    try {
      const res = await esignAPI.listSubmissions();
      setSubmissions(res.data);
    } catch {
      toast.error('Failed to load signatures');
    } finally {
      setSubsLoading(false);
    }
  };

  useEffect(() => {
    loadDocs();
    loadSubmissions();
  }, []);

  const handleDownload = async (doc) => {
    try {
      await documentsAPI.download(doc._id, doc.filename);
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <Layout title="eSignature">
      <div className="p-6 max-w-6xl mx-auto space-y-4">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            eSignature
          </h1>
          <p className="text-sm text-[#5C6670] mt-0.5">
            {templates.length} template{templates.length !== 1 ? 's' : ''} · {submissions.length} signature request{submissions.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Split screen — templates on the left, in-progress signing on the right */}
        <div className="flex flex-col lg:flex-row gap-5">

          {/* Templates */}
          <div className="lg:w-[340px] flex-shrink-0">
            <div className="flex items-center gap-1.5 mb-2">
              <LayoutTemplate className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-semibold text-[#1E2A33]">Templates</h3>
              <span className="text-xs text-[#9AA3AB]">({templates.length})</span>
            </div>
            {loading ? (
              <div className="text-center py-12 text-[#5C6670] text-sm">Loading…</div>
            ) : templates.length === 0 ? (
              <Card>
                <CardContent className="py-8 px-4 text-center text-xs text-[#5C6670]">
                  No templates yet. Upload a document on the Documents page, then use "Save as template" to add it here — every template is enabled for e-signature by default.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {templates.map((doc) => (
                  <Card key={doc._id} className="overflow-hidden">
                    <CardContent className="py-2.5 px-3">
                      <div className="flex items-center gap-2 mb-2">
                        <LayoutTemplate className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                        <span className="text-sm font-medium text-[#1E2A33] truncate flex-1">{doc.title}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => setPreviewDoc(doc)}>
                          Preview
                        </Button>
                        <Button size="sm" className="h-7 text-xs flex-1 bg-[#A62B1F] hover:bg-[#7c55e0] text-white" onClick={() => setSignDoc(doc)}>
                          Send
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-amber-600 hover:bg-amber-50 flex-shrink-0" title="Edit template" onClick={() => setTemplateDoc(doc)}>
                          <LayoutTemplate className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Out for signature */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-2">
              <Clock className="w-4 h-4 text-[#A62B1F]" />
              <h3 className="text-sm font-semibold text-[#1E2A33]">Out for Signature</h3>
              <span className="text-xs text-[#9AA3AB]">({submissions.length})</span>
            </div>
            <div className="space-y-3">
              {subsLoading ? (
                <div className="text-center py-12 text-[#5C6670]">Loading signatures…</div>
              ) : submissions.length === 0 ? (
                <div className="text-center py-12 text-[#5C6670]">No signature requests yet.</div>
              ) : (
                <div className="space-y-2">
                  {submissions.map(sub => (
                    <Card key={sub._id} className="overflow-hidden">
                      <CardContent className="py-3 px-4">
                        {/* Row 1: title + status badge */}
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <button
                            className="font-semibold text-[#1E2A33] text-sm hover:text-[#A62B1F] hover:underline text-left leading-tight"
                            onClick={() => setViewSubId(sub._id)}
                          >{sub.document_title}</button>
                          <div className="flex-shrink-0">
                            <ESignBadge status={sub.status} />
                          </div>
                        </div>

                        {/* Row 2: party info + dates */}
                        <div className="flex items-center justify-between gap-2 mb-2.5">
                          <p className="text-xs text-[#5C6670] truncate min-w-0">
                            {(sub.parties || []).map((p, i) => (
                              <span key={p.id || i}>
                                {i > 0 && ', '}
                                <span className={p.status === 'not_required' ? 'text-[#9AA3AB] line-through' : p.status !== 'pending' ? 'text-green-600 font-medium' : ''}>
                                  {p.name || p.email}{p.role === 'reviewer' ? ' (reviewer)' : ''}
                                </span>
                              </span>
                            ))}
                            {sub.executor_email && sub.executor_email !== sub.sent_by && (
                              <span className="text-[#F97316]"> · Executor: {sub.executor_name || sub.executor_email}</span>
                            )}
                          </p>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs text-[#5C6670]">Sent {safeFormat(sub.sent_at, 'MMM d')}</p>
                            {sub.executed_at && <p className="text-xs text-green-600 font-medium">Executed {safeFormat(sub.executed_at, 'MMM d')}</p>}
                          </div>
                        </div>

                        {/* Row 3: action buttons */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button size="sm" variant="outline" className="text-xs h-7 flex-1 min-w-0"
                            onClick={() => setViewSubId(sub._id)}>
                            View
                          </Button>
                          {(sub.status === 'pending' || sub.status === 'partially_signed') && (sub.parties || []).some(p => p.status === 'pending') && (
                            <Button size="sm" variant="outline" className="text-xs h-7 flex-1 min-w-0"
                              onClick={() => {
                                const nextPending = sub.parties.find(p => p.status === 'pending');
                                navigator.clipboard.writeText(nextPending.signing_url);
                                toast.success('Link copied');
                              }}>
                              Copy link
                            </Button>
                          )}
                          {['pending', 'partially_signed', 'awaiting_execution'].includes(sub.status) && (
                            <Button size="sm" variant="outline" className="text-xs h-7 flex-1 min-w-0" onClick={() => setManageSub(sub)}>
                              Manage
                            </Button>
                          )}
                          {sub.status === 'awaiting_execution' && (
                            <Button size="sm" className="text-xs h-7 flex-1 min-w-0 bg-[#F97316] hover:bg-[#ea6d0e] text-white"
                              onClick={() => setCountersignId(sub._id)}>
                              Sign my part
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Send for signature */}
      {signDoc && (
        <PrepareDocumentModal
          doc={signDoc}
          mode="send"
          onClose={() => setSignDoc(null)}
          onSent={() => { loadDocs(); loadSubmissions(); }}
        />
      )}

      {/* Preview */}
      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc}
          onClose={() => setPreviewDoc(null)}
          onDownload={handleDownload}
        />
      )}

      {/* Submission viewer */}
      {viewSubId && (
        <SubmissionViewerModal
          submissionId={viewSubId}
          onClose={() => setViewSubId(null)}
        />
      )}

      {/* Resend, correct an address, or release people whose signature isn't needed */}
      {manageSub && (
        <ManageSigningModal
          submission={manageSub}
          onClose={() => setManageSub(null)}
          onChanged={() => { loadSubmissions(); loadDocs(); }}
        />
      )}

      {/* Counter-sign modal */}
      {countersignId && (
        <CountersignModal
          submissionId={countersignId}
          onClose={() => setCountersignId(null)}
          onDone={() => { setCountersignId(null); loadSubmissions(); loadDocs(); }}
        />
      )}

      {/* Make / edit template */}
      {templateDoc && (
        <PrepareDocumentModal
          doc={templateDoc}
          mode="template"
          onClose={() => setTemplateDoc(null)}
          onSent={loadDocs}
        />
      )}
    </Layout>
  );
}
